import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RequestStatus } from '../../shared/enums/request-status';
import type { ResponseErrorWithHandled } from '../../shared/types';
import {
  createDwdWorkspaceRequest,
  createMicrosoftWorkspaceRequest,
  disconnectDwdEmailRequest,
  getConnectedEmailsRequest,
  getDwdSyncPreviewRequest,
  getDwdWorkspacesRequest,
  getMicrosoftBulkConsentUrlRequest,
  reconnectDwdEmailRequest,
  syncDwdWorkspaceRequest,
} from './extra-actions';
import {
  getDomainEmailsKey,
  getSyncPreviewFromResponse,
  getWorkspaceIdFromResponse,
} from './helpers/dwd-workspace-response';
import type { DomainEmailsState, DwdSyncPreviewResponse, DwdWorkspaceListItem } from './types';

type RequestState = {
  status: RequestStatus;
  message: string | null;
  error: ResponseErrorWithHandled | null;
};

type State = {
  getDwdWorkspacesRequest: RequestState;
  dwdWorkspacesList: DwdWorkspaceListItem[];
  createDwdWorkspaceRequest: RequestState;
  createMicrosoftWorkspaceRequest: RequestState;
  workspaceId: string | null;
  getDwdSyncPreviewRequest: RequestState;
  dwdSyncPreview: DwdSyncPreviewResponse | null;
  syncDwdWorkspaceRequest: RequestState;
  getMicrosoftBulkConsentUrlRequest: RequestState;
  microsoftBulkConsentUrl: string | null;
  connectedEmailsByDomain: Record<string, DomainEmailsState>;
  disconnectDwdEmailRequest: RequestState;
  reconnectDwdEmailRequest: RequestState;
};

const initialRequestState: RequestState = {
  status: RequestStatus.Ideal,
  message: null,
  error: null,
};

const initialState: State = {
  getDwdWorkspacesRequest: { ...initialRequestState },
  dwdWorkspacesList: [],
  createDwdWorkspaceRequest: { ...initialRequestState },
  createMicrosoftWorkspaceRequest: { ...initialRequestState },
  workspaceId: null,
  getDwdSyncPreviewRequest: { ...initialRequestState },
  dwdSyncPreview: null,
  syncDwdWorkspaceRequest: { ...initialRequestState },
  getMicrosoftBulkConsentUrlRequest: { ...initialRequestState },
  microsoftBulkConsentUrl: null,
  connectedEmailsByDomain: {},
  disconnectDwdEmailRequest: { ...initialRequestState },
  reconnectDwdEmailRequest: { ...initialRequestState },
};

const connectMailboxSlice = createSlice({
  name: 'connectMailbox',
  initialState,
  reducers: {
    setDwdWorkspaceId: (state, action: PayloadAction<string>) => {
      state.workspaceId = action.payload;
    },
    resetDwdWorkspaceState: (state) => {
      state.createDwdWorkspaceRequest = { ...initialRequestState };
      state.createMicrosoftWorkspaceRequest = { ...initialRequestState };
      state.workspaceId = null;
      state.getDwdSyncPreviewRequest = { ...initialRequestState };
      state.dwdSyncPreview = null;
      state.syncDwdWorkspaceRequest = { ...initialRequestState };
      state.getMicrosoftBulkConsentUrlRequest = { ...initialRequestState };
      state.microsoftBulkConsentUrl = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getDwdWorkspacesRequest.pending, (state) => {
      state.getDwdWorkspacesRequest.status = RequestStatus.Pending;
      state.getDwdWorkspacesRequest.message = null;
      state.getDwdWorkspacesRequest.error = null;
    });
    builder.addCase(getDwdWorkspacesRequest.fulfilled, (state, action) => {
      state.getDwdWorkspacesRequest.status = RequestStatus.Succeeded;
      state.getDwdWorkspacesRequest.message = null;
      state.getDwdWorkspacesRequest.error = null;
      state.dwdWorkspacesList = action.payload;
    });
    builder.addCase(getDwdWorkspacesRequest.rejected, (state, action) => {
      state.getDwdWorkspacesRequest.status = RequestStatus.Failed;
      state.getDwdWorkspacesRequest.message = action.payload?.message ?? null;
      state.getDwdWorkspacesRequest.error =
        action.payload && !action.payload.isHandled ? action.payload : null;
    });

    builder.addCase(createDwdWorkspaceRequest.pending, (state) => {
      state.createDwdWorkspaceRequest.status = RequestStatus.Pending;
      state.createDwdWorkspaceRequest.message = null;
      state.createDwdWorkspaceRequest.error = null;
    });
    builder.addCase(createDwdWorkspaceRequest.fulfilled, (state, action) => {
      state.createDwdWorkspaceRequest.status = RequestStatus.Succeeded;
      state.createDwdWorkspaceRequest.message = action.payload.message ?? null;
      state.createDwdWorkspaceRequest.error = null;
      state.workspaceId = getWorkspaceIdFromResponse(action.payload);
    });
    builder.addCase(createDwdWorkspaceRequest.rejected, (state, action) => {
      state.createDwdWorkspaceRequest.status = RequestStatus.Failed;
      state.createDwdWorkspaceRequest.message = action.payload?.message ?? null;
      state.createDwdWorkspaceRequest.error =
        action.payload && !action.payload.isHandled ? action.payload : null;
    });

    builder.addCase(createMicrosoftWorkspaceRequest.pending, (state) => {
      state.createMicrosoftWorkspaceRequest.status = RequestStatus.Pending;
      state.createMicrosoftWorkspaceRequest.message = null;
      state.createMicrosoftWorkspaceRequest.error = null;
    });
    builder.addCase(createMicrosoftWorkspaceRequest.fulfilled, (state, action) => {
      state.createMicrosoftWorkspaceRequest.status = RequestStatus.Succeeded;
      state.createMicrosoftWorkspaceRequest.message = null;
      state.createMicrosoftWorkspaceRequest.error = null;
      state.workspaceId = action.payload;
    });
    builder.addCase(createMicrosoftWorkspaceRequest.rejected, (state, action) => {
      state.createMicrosoftWorkspaceRequest.status = RequestStatus.Failed;
      state.createMicrosoftWorkspaceRequest.message =
        action.payload?.message ?? null;
      state.createMicrosoftWorkspaceRequest.error =
        action.payload && !action.payload.isHandled ? action.payload : null;
    });

    builder.addCase(getDwdSyncPreviewRequest.pending, (state) => {
      state.getDwdSyncPreviewRequest.status = RequestStatus.Pending;
      state.getDwdSyncPreviewRequest.message = null;
      state.getDwdSyncPreviewRequest.error = null;
    });
    builder.addCase(getDwdSyncPreviewRequest.fulfilled, (state, action) => {
      state.getDwdSyncPreviewRequest.status = RequestStatus.Succeeded;
      state.getDwdSyncPreviewRequest.message = action.payload.message ?? null;
      state.getDwdSyncPreviewRequest.error = null;
      state.dwdSyncPreview = getSyncPreviewFromResponse(action.payload);
    });
    builder.addCase(getDwdSyncPreviewRequest.rejected, (state, action) => {
      state.getDwdSyncPreviewRequest.status = RequestStatus.Failed;
      state.getDwdSyncPreviewRequest.message = action.payload?.message ?? null;
      state.getDwdSyncPreviewRequest.error =
        action.payload && !action.payload.isHandled ? action.payload : null;
    });

    builder.addCase(syncDwdWorkspaceRequest.pending, (state) => {
      state.syncDwdWorkspaceRequest.status = RequestStatus.Pending;
      state.syncDwdWorkspaceRequest.message = null;
      state.syncDwdWorkspaceRequest.error = null;
    });
    builder.addCase(syncDwdWorkspaceRequest.fulfilled, (state, action) => {
      state.syncDwdWorkspaceRequest.status = RequestStatus.Succeeded;
      state.syncDwdWorkspaceRequest.message = action.payload.message ?? null;
      state.syncDwdWorkspaceRequest.error = null;
    });
    builder.addCase(syncDwdWorkspaceRequest.rejected, (state, action) => {
      state.syncDwdWorkspaceRequest.status = RequestStatus.Failed;
      state.syncDwdWorkspaceRequest.message = action.payload?.message ?? null;
      state.syncDwdWorkspaceRequest.error =
        action.payload && !action.payload.isHandled ? action.payload : null;
    });

    builder.addCase(getMicrosoftBulkConsentUrlRequest.pending, (state) => {
      state.getMicrosoftBulkConsentUrlRequest.status = RequestStatus.Pending;
      state.getMicrosoftBulkConsentUrlRequest.message = null;
      state.getMicrosoftBulkConsentUrlRequest.error = null;
      state.microsoftBulkConsentUrl = null;
    });
    builder.addCase(getMicrosoftBulkConsentUrlRequest.fulfilled, (state, action) => {
      state.getMicrosoftBulkConsentUrlRequest.status = RequestStatus.Succeeded;
      state.getMicrosoftBulkConsentUrlRequest.message = null;
      state.getMicrosoftBulkConsentUrlRequest.error = null;
      state.microsoftBulkConsentUrl = action.payload;
    });
    builder.addCase(getMicrosoftBulkConsentUrlRequest.rejected, (state, action) => {
      state.getMicrosoftBulkConsentUrlRequest.status = RequestStatus.Failed;
      state.getMicrosoftBulkConsentUrlRequest.message =
        action.payload?.message ?? null;
      state.getMicrosoftBulkConsentUrlRequest.error =
        action.payload && !action.payload.isHandled ? action.payload : null;
    });

    builder.addCase(getConnectedEmailsRequest.pending, (state, action) => {
      const { workspaceId, domain } = action.meta.arg;
      const key = getDomainEmailsKey(workspaceId, domain?.trim() || 'all');
      const existing = state.connectedEmailsByDomain[key];

      state.connectedEmailsByDomain[key] = {
        data: existing?.data ?? [],
        total: existing?.total ?? 0,
        page: action.meta.arg.page ?? existing?.page ?? 1,
        limit: action.meta.arg.limit ?? existing?.limit ?? 50,
        status: RequestStatus.Pending,
      };
    });
    builder.addCase(getConnectedEmailsRequest.fulfilled, (state, action) => {
      const { key, emails } = action.payload;

      if (!emails) {
        return;
      }

      state.connectedEmailsByDomain[key] = {
        data: emails.data,
        total: emails.total,
        page: emails.page,
        limit: emails.limit,
        status: RequestStatus.Succeeded,
      };
    });
    builder.addCase(getConnectedEmailsRequest.rejected, (state, action) => {
      const { workspaceId, domain } = action.meta.arg;
      const key = getDomainEmailsKey(workspaceId, domain?.trim() || 'all');
      const existing = state.connectedEmailsByDomain[key];

      state.connectedEmailsByDomain[key] = {
        data: existing?.data ?? [],
        total: existing?.total ?? 0,
        page: existing?.page ?? 1,
        limit: existing?.limit ?? 50,
        status: RequestStatus.Failed,
      };
    });

    builder.addCase(disconnectDwdEmailRequest.pending, (state) => {
      state.disconnectDwdEmailRequest.status = RequestStatus.Pending;
      state.disconnectDwdEmailRequest.message = null;
      state.disconnectDwdEmailRequest.error = null;
    });
    builder.addCase(disconnectDwdEmailRequest.fulfilled, (state, action) => {
      state.disconnectDwdEmailRequest.status = RequestStatus.Succeeded;
      state.disconnectDwdEmailRequest.message = action.payload.message ?? null;
      state.disconnectDwdEmailRequest.error = null;
    });
    builder.addCase(disconnectDwdEmailRequest.rejected, (state, action) => {
      state.disconnectDwdEmailRequest.status = RequestStatus.Failed;
      state.disconnectDwdEmailRequest.message = action.payload?.message ?? null;
      state.disconnectDwdEmailRequest.error =
        action.payload && !action.payload.isHandled ? action.payload : null;
    });

    builder.addCase(reconnectDwdEmailRequest.pending, (state) => {
      state.reconnectDwdEmailRequest.status = RequestStatus.Pending;
      state.reconnectDwdEmailRequest.message = null;
      state.reconnectDwdEmailRequest.error = null;
    });
    builder.addCase(reconnectDwdEmailRequest.fulfilled, (state, action) => {
      state.reconnectDwdEmailRequest.status = RequestStatus.Succeeded;
      state.reconnectDwdEmailRequest.message = action.payload.message ?? null;
      state.reconnectDwdEmailRequest.error = null;
    });
    builder.addCase(reconnectDwdEmailRequest.rejected, (state, action) => {
      state.reconnectDwdEmailRequest.status = RequestStatus.Failed;
      state.reconnectDwdEmailRequest.message = action.payload?.message ?? null;
      state.reconnectDwdEmailRequest.error =
        action.payload && !action.payload.isHandled ? action.payload : null;
    });
  },
});

export const { setDwdWorkspaceId, resetDwdWorkspaceState } = connectMailboxSlice.actions;
export default connectMailboxSlice.reducer;
